import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { searchBill, deleteBill } from '../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import AppTable from '../../components/AppTable/AppTable';
import { setPagination } from '../../redux/Actions/TableAction';
import { getElevateUser, getSelectedUnit } from '../../storage/Storage';
import toast from 'react-hot-toast';
import EditButton from '../../components/Edit/EditButton';
import DeleteButton from '../../components/Delete/DeleteButton';
import { hasPermission } from '../../utils/permissionUtils';

function StaffBillManagement() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);
    const [isSearching, setIsSearching] = useState(false);

    // Permission checks
    const canEditBills = hasPermission('staffBilling', 'write');
    const canReadBills = hasPermission('staffBilling', 'read');
    const canDeleteBills = hasPermission('staffBilling', 'delete');
    const canAddBills = hasPermission('staffBilling', 'write');
    const hasAnyPermission = canEditBills || canDeleteBills || canReadBills;

    console.log({ canEditBills, canReadBills, canDeleteBills, canAddBills });

    useEffect(() => {
        if (canReadBills) {
            getBills();
        }
    }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit, canReadBills]);

    const getBills = () => {
        setIsSearching(true);

        const searchObj = {
            unitId: getSelectedUnit()?._id,
            status: 'pending',
            collab: getElevateUser()?._id
        };

        const json = {
            page: TableDataReducer.pagination.page,
            limit: TableDataReducer.pagination.limit,
            search: searchObj
        };

        HitApi(json, searchBill).then((res) => {
            if (res?.statusCode === 200) {
                dispatch(setApiJson(res?.data?.docs || []));

                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit,
                };

                dispatch(setPagination(paginationData));
            } else {
                dispatch(setApiJson([]));
            }
        }).catch(error => {
            console.error('Error fetching bills:', error);
            toast.error("Error fetching bills. Please try again.");
            dispatch(setApiJson([]));
        }).finally(() => {
            setIsSearching(false);
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '₹0';
        return `₹${Number(amount).toFixed(2)}`;
    };

    const renderPaymentStatus = (payment) => {
        const status = payment?.paymentStatus || 'Pending';
        let bgColor = 'bg-yellow-100';
        let textColor = 'text-yellow-800';

        if (status === 'Paid') {
            bgColor = 'bg-green-100';
            textColor = 'text-green-800';
        } else if (status === 'Partial') {
            bgColor = 'bg-orange-100';
            textColor = 'text-orange-800';
        } else if (status === 'Unpaid') {
            bgColor = 'bg-red-100';
            textColor = 'text-red-800';
        }

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {status}
            </span>
        );
    };

    const renderBillStatus = (status) => {
        const statusText = status || 'pending';
        let bgColor = 'bg-gray-100';
        let textColor = 'text-gray-800';

        if (statusText === 'completed') {
            bgColor = 'bg-green-100';
            textColor = 'text-green-800';
        } else if (statusText === 'cancelled') {
            bgColor = 'bg-red-100';
            textColor = 'text-red-800';
        }

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {statusText.charAt(0).toUpperCase() + statusText.slice(1)}
            </span>
        );
    };

    const handleEditBill = (billData) => {
        if (!canEditBills) {
            toast.error('You do not have permission to edit bills');
            return;
        }
        navigate(`/staffBilling/edit/${billData._id}`, { state: { billData } });
    };

    const handleDeleteBill = async (billData) => {
        if (!canDeleteBills) {
            toast.error('You do not have permission to delete bills');
            return;
        }

        const isConfirmed = window.confirm(`Are you sure you want to delete bill ${billData.billNumber || 'this bill'}?`);

        if (!isConfirmed) {
            return;
        }

        try {
            toast.loading('Deleting bill...', { id: 'delete-bill' });

            const json = {
                _id: billData._id
            };

            const response = await HitApi(json, deleteBill);

            if (response?.statusCode === 200 || response?.statusCode === 201) {
                toast.success('Bill deleted successfully!', {
                    id: 'delete-bill',
                    duration: 3000
                });

                getBills();
            } else {
                toast.error(response?.message || 'Failed to delete bill', {
                    id: 'delete-bill',
                    duration: 4000
                });
            }
        } catch (error) {
            console.error('Error deleting bill:', error);
            toast.error('Network error. Please try again.', {
                id: 'delete-bill',
                duration: 4000
            });
        }
    };

    const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

    const baseTableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "center",
            render: (_, __, index) => {
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Bill Details",
            key: "billNumber",
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900 text-sm">{value || 'N/A'}</div>
                    <div className="text-xs text-gray-500">
                        {row.transactionId || 'No transaction ID'}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                        {formatDate(row.timestamp)}
                    </div>
                </div>
            )
        },
        {
            title: "Items",
            key: "items",
            align: "start",
            render: (_, row) => (
                <div className="text-start">
                    {row.services && row.services.length > 0 && (
                        <div className="text-xs">
                            <span className="font-medium text-blue-600">Services: </span>
                            <span className="text-gray-900">{row.services.length}</span>
                        </div>
                    )}
                    {row.products && row.products.length > 0 && (
                        <div className="text-xs">
                            <span className="font-medium text-purple-600">Products: </span>
                            <span className="text-gray-900">{row.products.length}</span>
                        </div>
                    )}
                    {row.newMemberships && row.newMemberships.length > 0 && (
                        <div className="text-xs">
                            <span className="font-medium text-green-600">Memberships: </span>
                            <span className="text-gray-900">{row.newMemberships.length}</span>
                        </div>
                    )}
                    {(!row.services?.length && !row.products?.length && !row.newMemberships?.length) && (
                        <span className="text-xs text-gray-400">No items</span>
                    )}
                </div>
            )
        },
        {
            title: "Amount",
            key: "calculations",
            render: (calculations) => {
                const finalAmount = calculations?.totals?.finalAmount || 0;
                return (
                    <div className="text-sm font-bold text-gray-900">
                        {formatCurrency(finalAmount)}
                    </div>
                );
            }
        },
        {
            title: "Status",
            key: "status",
            align: "start",
            render: (value) => (
                <div className="text-start">
                    {renderBillStatus(value)}
                </div>
            )
        },
        {
            title: "Time",
            key: "timestamp",
            align: "start",
            render: (value) => (
                <div className="text-start text-xs text-gray-600">
                    {formatTime(value)}
                </div>
            )
        }
    ];

    // Add Actions column only if user has any permission
    const tableHeaders = [
        ...baseTableHeaders,
        {
            title: "Action",
            key: "action",
            align: "center",
            width: "120px",
            render: (_, row) => {
                if (!hasAnyPermission) {
                    return (
                        <div className="flex justify-center">
                            <span className="text-sm text-gray-500">Loading...</span>
                        </div>
                    );
                }

                return (
                    <div className="flex justify-center gap-2">
                        {canEditBills && (
                            <EditButton
                                onEdit={() => handleEditBill(row)}
                                itemName="bill"
                                tooltip="Edit Bill"
                                disabled={isSearching}
                            />
                        )}
                        {canDeleteBills && (
                            <DeleteButton
                                onDelete={() => handleDeleteBill(row)}
                                itemName="bill"
                                disabled={isSearching}
                            />
                        )}
                    </div>
                );
            }
        }
    ];

    const handleRowClick = (row) => {
        // Navigate to bill details if needed
        // navigate(`/staff-billing/details/${row._id}`);
    };

    const billsData = Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : [];

    return (
        <div className="md:p-5">
            <PageHeader
                title={'Staff Bill Management'}
                description={'View and manage pending staff bills'}
            />

            {!hasAnyPermission ? (
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg">Loading permissions...</div>
                </div>
            ) : (
                <div>
                    <AppTable
                        TH={tableHeaders}
                        TD={billsData}
                        onRowClick={handleRowClick}
                        isLoading={isSearching}
                        emptyMessage="No pending bills found"
                        buttonText={canAddBills && "Add Bill"}
                        navigateTo={canAddBills && "/staffBilling/add"}
                    />
                </div>
            )}
        </div>
    );
}

export default StaffBillManagement;